/**
 * Admissions CRM — User Candidacies API contract (plan #006, U2).
 *
 * GET /v1/schools/:school_public_id/school-users/:school_user_public_id/enrollment
 *
 * Admin-only, school-scoped. Given a school-user publicId, returns the list of
 * that user's `EnrollmentCandidate` rows (one per entering year — BB KTD2
 * fan-out), most-recent year first. Lets the User Details modal discover whether
 * a user is/was a candidate and drive its in-modal year switcher; the heavy
 * checklist/history/family detail stays on the existing
 * `/v1/enrollment/candidates/:publicId` endpoint, keyed by `candidatePublicId`.
 *
 * Empty array for a non-candidate user (200). School is the active-school
 * request context (`x-school-id`), never a caller-supplied id — cross-school
 * resolves to an empty list (no info leak).
 */

import { z } from 'zod'

/** One of the user's candidacies (one entering-year row). */
export const UserCandidacySchema = z.object({
  /** Candidate row publicId — addresses the candidate-detail endpoint. */
  candidatePublicId: z.string(),
  /** Entering-year label (e.g. "2026 - 2027"). */
  enteringYear: z.string(),
  /** Snapshot stage label joined from the catalog (KTD11), or null. */
  statusDescription: z.string().nullable(),
  /**
   * Whether this candidacy is ENROLLED (the conversion / win, R9). Derived from
   * the MIRROR stage — the candidate's `statusId` joined to the catalog's
   * Enrolled-stage set (KTD11) — never from roster-login presence.
   */
  isConverted: z.boolean()
})
export type UserCandidacy = z.infer<typeof UserCandidacySchema>

export const GetUserCandidaciesResponseSchema = z.object({
  /** The user's candidacies, most-recent entering year first. Empty for non-candidates. */
  candidacies: z.array(UserCandidacySchema)
})
export type GetUserCandidaciesResponse = z.infer<typeof GetUserCandidaciesResponseSchema>
